/** Admin panel wall-clock timezone — India Standard Time (no DST). */
export const APP_TIMEZONE = "Asia/Kolkata";
export const APP_TZ_LABEL = "IST";

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

function asDate(value) {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function partMap(date, options) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    ...options,
  }).formatToParts(date);
  const map = {};
  for (const p of parts) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  return map;
}

/** Format a date for display in IST (e.g. 11 Oct 2026). */
export function formatDateIST(value) {
  const d = asDate(value);
  if (!d) return "";
  // DATEONLY YYYY-MM-DD — show as calendar day without TZ shift
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, day] = value.split("-").map(Number);
    return new Date(y, m - 1, day).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return d.toLocaleDateString("en-IN", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Format date+time for display in IST. */
export function formatDateTimeIST(value) {
  const d = asDate(value);
  if (!d) return "";
  return d.toLocaleString("en-IN", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

/** YYYY-MM-DD in IST for <input type="date">. */
export function toDateInputIST(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = asDate(value);
  if (!d) return "";
  const p = partMap(d, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return `${p.year}-${p.month}-${p.day}`;
}

/** YYYY-MM-DDTHH:mm in IST for <input type="datetime-local">. */
export function toDateTimeLocalIST(value) {
  const d = asDate(value);
  if (!d) return "";
  const p = partMap(d, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  let hour = p.hour || "00";
  if (hour === "24") hour = "00";
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute || "00"}`;
}

/**
 * Interpret a datetime-local string as IST wall time and return UTC ISO.
 * @param {string} local "YYYY-MM-DDTHH:mm"
 */
export function istDateTimeLocalToISO(local) {
  if (!local) return null;
  const [datePart, timePart = "00:00"] = local.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  if (![y, m, d, hh, mm].every((n) => Number.isFinite(n))) return null;
  const utcMs = Date.UTC(y, m - 1, d, hh, mm, 0, 0) - IST_OFFSET_MS;
  return new Date(utcMs).toISOString();
}
